import { useActivePromo } from '@/lib/promoState';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Lock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { trackEvent, getAttribution } from '@/lib/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadKrypton = (jsUrl, publicKey) => new Promise((resolve, reject) => {
  if (window.KR) { resolve(window.KR); return; }
  const base = jsUrl.replace(/\/stable\/.*$/, '');
  if (!document.querySelector('link[data-kr-theme]')) {
    const css = document.createElement('link');
    css.rel = 'stylesheet'; css.href = `${base}/ext/neon-reset.min.css`; css.setAttribute('data-kr-theme', '1');
    document.head.appendChild(css);
  }
  const s = document.createElement('script');
  s.src = jsUrl;
  s.setAttribute('kr-public-key', publicKey);
  s.setAttribute('kr-no-card-logo-header', 'true');
  s.onload = () => {
    const theme = document.createElement('script');
    theme.src = `${base}/ext/neon.js`;
    theme.onload = () => resolve(window.KR);
    theme.onerror = () => resolve(window.KR);
    document.head.appendChild(theme);
  };
  s.onerror = () => reject(new Error('krypton'));
  document.head.appendChild(s);
});

/**
 * PayZen (Lyra REST V4) — per-store PSP.
 *  - `mode="embedded"`: krypton-client card fields rendered in-page from a
 *    server-side formToken; KR.onSubmit → /public/payzen/confirm → success page.
 *  - `mode="redirect"`: Charge/CreatePaymentOrder hosted page, then back via
 *    /public/payzen/return.
 */
export const PayzenPayForm = ({
  items, total, form, hasRequiredCustomerInfo, source, shippingOptionId,
  shippingChosen, accentColor, storeId, storeSlug, currency = 'eur', payzenCfg, t,
}) => {
  const mode = payzenCfg?.mode || 'embedded';
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [formReady, setFormReady] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const txIdRef = useRef('');
  const formKeyRef = useRef('');
  const hostRef = useRef(null);
  const promoCode = useActivePromo();

  const payload = useCallback(() => ({
    items,
    customer: {
      email: form.email, first_name: form.first_name, last_name: form.last_name,
      address1: form.address1, city: form.city, postal_code: form.postal_code,
      country: (form.country || 'FR').toUpperCase(), phone: form.phone || '',
    },
    shipping_option_id: shippingOptionId, source, currency, attribution: getAttribution(), promo_code: promoCode,
  }), [items, form, shippingOptionId, source, currency, promoCode]);

  const fail = (e, code) => {
    const msg = e?.response?.data?.detail || e?.message || t.err_payment;
    try { trackEvent('payment_failed', { storeId, slug: storeSlug, amount: total, source, failureCode: code, failureMessage: msg }); } catch { /* noop */ }
    setErrMsg(msg);
    toast.error(msg);
  };

  const confirm = useCallback(async (krAnswer, krHash) => {
    setWaiting(true);
    try {
      const { data } = await axios.post(`${API}/public/payzen/confirm`, { tx_id: txIdRef.current, kr_answer: krAnswer || '', kr_hash: krHash || '' });
      if (data?.payment_status === 'paid') {
        window.location.href = `/checkout/success?pi=${txIdRef.current}`;
        return;
      }
      if (data?.payment_status === 'failed') { setWaiting(false); setErrMsg(t.err_payment); return; }
      // still pending → poll a few times (IPN may lag)
      for (let i = 0; i < 10; i += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const p = await axios.get(`${API}/public/payzen/poll/${txIdRef.current}`);
        if (p.data?.payment_status === 'paid') { window.location.href = `/checkout/success?pi=${txIdRef.current}`; return; }
        if (p.data?.payment_status === 'failed') break;
      }
      setWaiting(false);
      setErrMsg(t.err_payment);
    } catch (e) { setWaiting(false); fail(e, 'payzen_confirm_error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Embedded: (re)build the form token whenever the amount / customer info changes.
  useEffect(() => {
    if (mode !== 'embedded' || !payzenCfg || !hasRequiredCustomerInfo || !shippingChosen) return undefined;
    const key = `${total}-${shippingOptionId}-${form.email}`;
    if (formKeyRef.current === key) return undefined;
    let cancelled = false;
    const id = setTimeout(async () => {
      formKeyRef.current = key;
      setFormReady(false);
      setErrMsg('');
      try {
        const { data } = await axios.post(`${API}/public/payzen/form-token`, payload());
        if (cancelled) return;
        txIdRef.current = data.tx_id;
        const KR = await loadKrypton(data.js_url, data.public_key);
        if (cancelled) return;
        await KR.removeForms?.();
        await KR.setFormConfig({ formToken: data.form_token, 'kr-language': (navigator.language || 'fr-FR'), 'kr-post-url-success': `${API}/public/payzen/return?tx=${data.tx_id}` });
        KR.onSubmit((res) => {
          try { trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source }); } catch { /* noop */ }
          confirm(res?.rawClientAnswer, res?.hash);
          return false;
        });
        KR.onError((err) => { setErrMsg(err?.errorMessage || t.err_payment); });
        await KR.renderElements('#payzen-kr-form');
        if (!cancelled) setFormReady(true);
      } catch (e) {
        if (!cancelled) { formKeyRef.current = ''; fail(e, 'payzen_form_error'); }
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, payzenCfg, hasRequiredCustomerInfo, shippingChosen, total, shippingOptionId, form.email]);

  useEffect(() => () => { try { window.KR?.removeForms?.(); } catch { /* noop */ } }, []);

  const payRedirect = async (e) => {
    e.preventDefault();
    if (!shippingChosen) { toast.error(t.toast_choose_shipping); return; }
    if (!hasRequiredCustomerInfo) { toast.error(t.toast_incomplete_info); return; }
    setProcessing(true);
    setErrMsg('');
    try {
      try { trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source }); } catch { /* noop */ }
      const { data } = await axios.post(`${API}/public/payzen/create-order`, payload());
      if (data?.redirect_url) { window.location.href = data.redirect_url; return; }
      throw new Error(t.err_payment);
    } catch (e2) { fail(e2, 'payzen_order_error'); setProcessing(false); }
  };

  const sym = currency === 'usd' ? '$' : (currency === 'gbp' ? '£' : '€');

  if (mode === 'embedded') {
    const gated = !hasRequiredCustomerInfo || !shippingChosen;
    return (
      <div className="space-y-3" data-testid="payzen-embedded">
        {gated && (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 flex items-start gap-2" data-testid="payzen-embedded-hint">
            <CreditCard className="h-4 w-4 mt-0.5 shrink-0 text-zinc-500" />
            <span>{t.payzen_embedded_hint}</span>
          </div>
        )}
        {!gated && !formReady && !errMsg && (
          <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="payzen-form-loading">
            <Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span>
          </div>
        )}
        <div ref={hostRef} className={gated ? 'hidden' : ''} style={{ '--kr-accent': accentColor || '#0A0A0A' }}>
          <div id="payzen-kr-form" className="kr-embedded" data-testid="payzen-kr-form">
            <div className="kr-pan" />
            <div className="kr-expiry" />
            <div className="kr-security-code" />
            <button type="button" className="kr-payment-button" style={{ backgroundColor: accentColor || '#0A0A0A' }} />
            <div className="kr-form-error" />
          </div>
        </div>
        {errMsg && <div data-testid="payzen-error" className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{errMsg}</div>}
        {waiting && (
          <p className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5" data-testid="payzen-waiting">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.processing}
          </p>
        )}
        <p className="text-[11px] text-zinc-400 text-center flex items-center justify-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-emerald-600" /> {t.secured_payzen}
        </p>
      </div>
    );
  }

  const disabled = processing || !shippingChosen;
  const btnLabel = processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t.processing}</>
    : !shippingChosen ? <>{t.choose_shipping_btn}</>
      : <><Lock className="h-4 w-4 mr-2" /> {t.pay} {total.toFixed(2)} {sym}</>;

  return (
    <form onSubmit={payRedirect} className="space-y-4" data-testid="payzen-pay-form">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 flex items-center justify-between gap-2" data-testid="payzen-methods">
        <span className="text-sm text-zinc-700">{t.secure_pay}</span>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-500">
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">CB</span>
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">VISA</span>
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">Mastercard</span>
        </div>
      </div>
      {errMsg && <div data-testid="payzen-error" className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{errMsg}</div>}
      <Button type="submit" disabled={disabled} data-testid="payzen-pay-button"
        style={{ backgroundColor: shippingChosen ? (accentColor || '#0A0A0A') : '#A1A1AA' }}
        className="hidden lg:flex w-full hover:opacity-90 text-white rounded-md h-12 text-base font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center">
        {btnLabel}
      </Button>
      <p className="hidden lg:block text-[11px] text-zinc-400 text-center">{t.secured_payzen}</p>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        <Button type="submit" disabled={disabled} data-testid="mobile-sticky-pay-button"
          style={{ backgroundColor: shippingChosen ? (accentColor || '#0A0A0A') : '#A1A1AA' }}
          className="w-full hover:opacity-90 text-white rounded-md h-14 text-[17px] font-semibold transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center">
          {btnLabel}
        </Button>
      </div>
    </form>
  );
};
